"use client"

import { AskLeoButton } from "@/components/ask-leo-button"
import { Button } from "@/components/ui/button"
import { INSPECTOR_PICKER_TRIGGER_CLASS } from "@/components/question-composer/composer-fields"
import {
  QuestionQualityFinding,
  QuestionQualitySuggestedEnhancements,
} from "@/components/question-quality/question-quality-finding"
import { cn } from "@/lib/utils"
import {
  authoringConfidenceScore,
  questionQualityFieldSiblings,
  questionQualityHomeFor,
  questionQualityOpportunities,
  questionQualityVisibleHomes,
  selectHomeRecommendations,
  type QuestionQualityAlternate,
  type QuestionQualityCheck,
  type QuestionQualityHistory,
} from "@/lib/question-quality"

export function QuestionQualityPane({
  checks,
  history,
  focusCheckId,
  disabled,
  onBack,
  onUse,
  onDismiss,
  onEnhanceWithLeo,
  onReplace,
  onRetagDifficulty,
}: {
  checks: QuestionQualityCheck[]
  history: QuestionQualityHistory | null
  focusCheckId?: string | null
  disabled?: boolean
  onBack?: () => void
  onUse: (
    check: QuestionQualityCheck,
    secondary?: boolean,
    alternate?: QuestionQualityAlternate,
  ) => void
  onDismiss: (checkId: string) => void
  onEnhanceWithLeo: (check?: QuestionQualityCheck) => void
  onReplace?: () => void
  onRetagDifficulty?: () => void
}) {
  const reviewCount = checks.filter((check) => check.severity === "review").length
  const nowScore = authoringConfidenceScore(checks)
  const working =
    history?.sat &&
    history.difficultyMatches &&
    !history.belowReviewFloor
  const preferVary =
    working || (history?.sat && (history.usedCount ?? 0) >= 3 && !history.belowReviewFloor)
  const homes = questionQualityVisibleHomes(checks)
  const focusedHome = questionQualityHomeFor(checks, focusCheckId)
  const opportunities = questionQualityOpportunities(checks, history)

  const runOpportunity = (id: string) => {
    const row = opportunities.find((item) => item.id === id)
    if (!row) return
    if (row.kind === "settings") {
      onRetagDifficulty?.()
      return
    }
    if (row.kind === "replace") {
      onReplace?.()
      return
    }
    const check = checks.find((item) => item.id === row.checkId)
    if (row.kind === "leo") {
      onEnhanceWithLeo(check)
      return
    }
    if (check && row.alternate) {
      onUse(
        check,
        row.alternate.actionKind === check.secondaryActionKind,
        row.alternate,
      )
    }
  }

  return (
    <div className="flex flex-col gap-6">
      {onBack ? (
        <div className="flex items-center gap-2">
          <Button type="button" size="sm" variant="ghost" onClick={onBack}>
            <i className="fa-light fa-chevron-left" aria-hidden="true" />
            Settings
          </Button>
        </div>
      ) : null}

      {history?.sat ? (
        <section className="flex flex-col gap-3" aria-labelledby="qq-history">
          <h3 id="qq-history" className="text-xs font-medium text-muted-foreground">
            History
          </h3>
          <ul className="flex flex-col gap-2 text-sm">
            {history.taggedDifficulty && history.measuredDifficultyPercent != null ? (
              <li>
                Tagged {history.taggedDifficulty}. Measured{" "}
                {history.measuredDifficultyPercent}% correct
                {history.difficultyMatches ? ". Performed as tagged." : "."}
              </li>
            ) : null}
            {history.pointBiserial != null ? (
              <li>
                Point-biserial {history.pointBiserial.toFixed(2)}
                {history.belowReviewFloor
                  ? ". Below the review floor."
                  : ". Working."}
              </li>
            ) : null}
            {history.usedCount != null && history.usedCount > 0 ? (
              <li>
                Used {history.usedCount}{" "}
                {history.usedCount === 1 ? "time" : "times"}
                {history.usedInSameCourse ? " in this course" : ""}.
              </li>
            ) : null}
          </ul>
          <div className="flex flex-wrap gap-2">
            {history.belowReviewFloor ? (
              <>
                <Button type="button" size="sm" onClick={() => onEnhanceWithLeo()}>
                  Enhance
                </Button>
                {onReplace ? (
                  <Button type="button" size="sm" variant="outline" onClick={onReplace}>
                    Replace
                  </Button>
                ) : null}
              </>
            ) : preferVary ? (
              <p className="text-xs text-muted-foreground">
                No Review checks needed from history. Prefer variants at Distribution
                rather than a rewrite.
              </p>
            ) : null}
          </div>
        </section>
      ) : null}

      <section className="flex flex-col gap-3" aria-labelledby="qq-checks">
        <h3 id="qq-checks" className="text-xs font-medium text-muted-foreground">
          Checks
        </h3>
        <p className="text-xs text-muted-foreground">
          Confidence score {nowScore}%
        </p>
        {homes.length === 0 ? (
          <p className="text-sm text-muted-foreground">No checks on this item.</p>
        ) : (
          <ul className="flex flex-col gap-3">
            {homes.map((home) => {
              const recs = selectHomeRecommendations(
                questionQualityFieldSiblings(checks, home),
                checks,
              )
              return (
                <li
                  key={home.id}
                  id={`qq-check-${home.id}`}
                  aria-current={focusedHome?.id === home.id ? "true" : undefined}
                  className={cn(
                    "rounded-2 border p-3",
                    focusedHome?.id === home.id
                      ? "border-brand bg-brand/10"
                      : "border-border",
                  )}
                >
                  <div className="flex flex-col gap-3">
                    <QuestionQualitySuggestedEnhancements
                      check={home}
                      siblingChecks={checks}
                      recs={recs}
                      onUse={(secondary, alternate) =>
                        onUse(home, secondary, alternate)
                      }
                      disabled={disabled}
                    />
                    <QuestionQualityFinding
                      check={home}
                      siblingChecks={checks}
                      surface="panel"
                      quiet={recs.length > 0}
                    />
                    <div className="flex flex-wrap gap-2">
                      {recs.length === 0 && home.actionKind === "open-leo" ? (
                        <AskLeoButton
                          size="sm"
                          label="Enhance with Leo"
                          showShortcut={false}
                          onClick={() => onEnhanceWithLeo(home)}
                          disabled={disabled}
                        />
                      ) : recs.length === 0 &&
                        home.actionKind === "none" &&
                        home.parameter === "format" ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => onRetagDifficulty?.()}
                        >
                          Change format
                        </Button>
                      ) : null}
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        disabled={disabled}
                        onClick={() => onDismiss(home.id)}
                      >
                        Dismiss
                      </Button>
                    </div>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </section>

      <section className="flex flex-col gap-3" aria-labelledby="qq-opportunities">
        <h3 id="qq-opportunities" className="text-xs font-medium text-muted-foreground">
          Enhancements
        </h3>
        {reviewCount === 0 && preferVary ? (
          <p className="text-sm text-muted-foreground">
            No Review checks. If this item already works, prefer variants at
            Distribution rather than a rewrite.
          </p>
        ) : opportunities.length === 0 ? (
          <p className="text-sm text-muted-foreground">No checks on this item.</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {opportunities.map((row) => (
              <li key={row.id}>
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => runOpportunity(row.id)}
                  className={cn(INSPECTOR_PICKER_TRIGGER_CLASS, "bg-background")}
                >
                  <span className="min-w-0 flex-1 truncate text-sm font-medium">
                    {row.label}
                  </span>
                  <i
                    className="fa-light fa-arrow-right text-xs text-muted-foreground"
                    aria-hidden="true"
                  />
                </button>
              </li>
            ))}
          </ul>
        )}
        <AskLeoButton
          size="sm"
          label="Enhance with Leo"
          showShortcut={false}
          onClick={() => onEnhanceWithLeo()}
          disabled={disabled}
        />
      </section>
    </div>
  )
}
